import { z } from 'zod';

import { PaginationSchema } from '../../../pagination.js';
import { CatalogProductVehicleApplicationListItemSchema } from '../catalog-generic-vehicle-application.schemas.js';

/** ADR-0044 / BR-PRD-29/30 — overlay tenant sobre a camada global + lookup barcode-first. */

export const LinkProductToCatalogBodySchema = z.object({
  catalogProductId: z.uuid(),
});

export const ProductCatalogLinkSummarySchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  status: z.enum(['UNVERIFIED', 'VERIFIED']),
});

export const LinkProductToCatalogResponseSchema = z.object({
  productId: z.uuid(),
  catalogProduct: ProductCatalogLinkSummarySchema,
});

export const GlobalCatalogLookupQuerySchema = PaginationSchema.extend({
  ean: z.string().trim().min(1).max(14).optional(),
  brandName: z.string().trim().min(1).max(120).optional(),
  mpn: z.string().trim().min(1).max(60).optional(),
  q: z.string().trim().min(1).max(200).optional(),
});

export const GlobalCatalogLookupItemSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  ean: z.string().nullable(),
  brandName: z.string().nullable(),
  mpn: z.string().nullable(),
  status: z.enum(['UNVERIFIED', 'VERIFIED']),
  matchType: z.enum(['EAN', 'BRAND_MPN', 'TEXT']),
});

export const GlobalCatalogLookupResponseSchema = z.object({
  items: z.array(GlobalCatalogLookupItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});

export type LinkProductToCatalogBody = z.infer<typeof LinkProductToCatalogBodySchema>;
export type ProductCatalogLinkSummary = z.infer<typeof ProductCatalogLinkSummarySchema>;
export type LinkProductToCatalogResponse = z.infer<typeof LinkProductToCatalogResponseSchema>;
export type GlobalCatalogLookupQuery = z.infer<typeof GlobalCatalogLookupQuerySchema>;
export type GlobalCatalogLookupItem = z.infer<typeof GlobalCatalogLookupItemSchema>;
export type GlobalCatalogLookupResponse = z.infer<typeof GlobalCatalogLookupResponseSchema>;

/** BR-PUR-28 — distinct brand names from the global catalog layer. */
export const ListGlobalCatalogBrandsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});

export const GlobalCatalogBrandItemSchema = z.object({
  name: z.string().min(1),
});

export const ListGlobalCatalogBrandsResponseSchema = z.object({
  items: z.array(GlobalCatalogBrandItemSchema),
});

export type ListGlobalCatalogBrandsQuery = z.infer<typeof ListGlobalCatalogBrandsQuerySchema>;
export type GlobalCatalogBrandItem = z.infer<typeof GlobalCatalogBrandItemSchema>;
export type ListGlobalCatalogBrandsResponse = z.infer<typeof ListGlobalCatalogBrandsResponseSchema>;

/** BR-PRD-99 — cadastro global de fabricantes (leitura autenticada). */
export const ListCatalogManufacturersQuerySchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});

export const CatalogManufacturerItemSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  slug: z.string().min(1),
});

export const ListCatalogManufacturersResponseSchema = z.object({
  items: z.array(CatalogManufacturerItemSchema),
});

export type ListCatalogManufacturersQuery = z.infer<typeof ListCatalogManufacturersQuerySchema>;
export type CatalogManufacturerItem = z.infer<typeof CatalogManufacturerItemSchema>;
export type ListCatalogManufacturersResponse = z.infer<
  typeof ListCatalogManufacturersResponseSchema
>;

const CatalogPrefillBrandSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().min(1),
});

const CatalogPrefillCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  breadcrumb: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      slug: z.string(),
    }),
  ),
});

const CatalogPrefillGenericSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  category: CatalogPrefillCategorySchema,
  ncmCode: z.string().nullable(),
  cestCode: z.string().nullable(),
  usefulLifeMeters: z.number().int().nullable(),
  usefulLifeMonths: z.number().int().nullable(),
  warrantyMonths: z.number().int().nullable(),
  warrantyMeters: z.number().int().nullable(),
  attributeSchema: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      type: z.enum(['NUMBER', 'TEXT', 'ENUM', 'BOOLEAN']),
      required: z.boolean(),
      unit: z.string().optional(),
      values: z.array(z.string()).optional(),
    }),
  ),
  attributeValues: z.array(
    z.object({
      attributeKey: z.string(),
      valueText: z.string(),
    }),
  ),
});

const PrefillNamedSuggestionSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().min(1),
});

const PrefillCategorySuggestionSchema = z.object({
  root: PrefillNamedSuggestionSchema.nullable(),
  leaf: PrefillNamedSuggestionSchema.nullable(),
});

const PrefillManufacturerSuggestionSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  origin: z.literal('ZR'),
});

const PrefillMeasurementUnitSuggestionSchema = z.object({
  id: z.uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  origin: z.literal('ZR'),
});

/** BR-PRD-105 — tenant-authenticated prefill of a global concrete piece. */
export const GlobalCatalogProductPrefillResponseSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  brand: CatalogPrefillBrandSchema.nullable(),
  mpn: z.string().nullable(),
  ean: z.string().nullable(),
  packageBarcode: z.string().nullable(),
  grossWeightGrams: z.number().int().nullable(),
  netWeightGrams: z.number().int().nullable(),
  notes: z.string().nullable(),
  genericProduct: CatalogPrefillGenericSchema.nullable(),
  categorySuggestion: PrefillCategorySuggestionSchema.nullable(),
  brandSuggestion: PrefillNamedSuggestionSchema.nullable(),
  manufacturerSuggestion: PrefillManufacturerSuggestionSchema.nullable(),
  measurementUnitSuggestion: PrefillMeasurementUnitSuggestionSchema.nullable(),
  vehicleApplications: z.array(CatalogProductVehicleApplicationListItemSchema),
});

export type GlobalCatalogProductPrefillResponse = z.infer<
  typeof GlobalCatalogProductPrefillResponseSchema
>;
